import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconDashboard, IconCompanies, IconProjects,
  IconContracts, IconTasks, IconChat, IconFunds, IconCalendar,
} from '../components/icons.jsx';

export const WORKSPACE_FUNDS = 'funds';
export const WORKSPACE_TRADE = 'trade';
export const WORKSPACE_GENERAL = 'general';
export const WORKSPACES = [WORKSPACE_FUNDS, WORKSPACE_TRADE, WORKSPACE_GENERAL];

const STORAGE_KEY = 'agevolami_workspace';
const LAST_PATH_KEY = 'agevolami_workspace_paths';

/** Home path when switching into a workspace with no remembered page. */
export const WORKSPACE_HOME = {
  [WORKSPACE_FUNDS]: '/funds',
  [WORKSPACE_TRADE]: '/projects',
  [WORKSPACE_GENERAL]: '/projects',
};

/**
 * Path ownership:
 * - exclusive funds routes force funds workspace
 * - exclusive trade routes force trade workspace
 * - shared routes keep the current workspace
 *
 * NOTE: /projects is deliberately NOT trade-exclusive — Trade and General
 * both live there, separated by projects.business_line rather than by URL.
 */
export function getWorkspaceForPath(pathname) {
  const p = pathname || '/';
  if (
    p === '/funds' || p.startsWith('/funds/')
    || p.startsWith('/fund-clients/')
  ) {
    return WORKSPACE_FUNDS;
  }
  if (
    p === '/contracts' || p.startsWith('/contracts/')
    || p === '/offers' || p.startsWith('/offers/')
    || p === '/exclusivities' || p.startsWith('/exclusivities/')
    || p === '/invoices' || p.startsWith('/invoices/')
  ) {
    return WORKSPACE_TRADE;
  }
  return null; // shared: dashboard, companies, projects, tasks, calendar, assistant…
}

export function isPathAllowedInWorkspace(pathname, workspace) {
  const owner = getWorkspaceForPath(pathname);
  if (!owner) return true;
  return owner === workspace;
}

function readStoredWorkspace() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (WORKSPACES.includes(v)) return v;
  } catch { /* private mode */ }
  return WORKSPACE_TRADE;
}

function readLastPaths() {
  try {
    const raw = localStorage.getItem(LAST_PATH_KEY);
    if (raw) return { ...WORKSPACE_HOME, ...JSON.parse(raw) };
  } catch { /* private mode */ }
  return { ...WORKSPACE_HOME };
}

function navGroupsFor(workspace) {
  const sharedTop = [
    { to: '/', label: 'Dashboard', end: true, Icon: IconDashboard },
    { to: '/companies', label: 'Companies', Icon: IconCompanies },
  ];
  const sharedBottom = [
    { to: '/calendar', label: 'Calendar', Icon: IconCalendar },
    { to: '/tasks', label: 'Tasks', Icon: IconTasks },
    { to: '/assistant', label: 'Assistant', Icon: IconChat },
  ];

  if (workspace === WORKSPACE_FUNDS) {
    return [
      { label: null, items: sharedTop },
      { label: 'Italy — Funds', items: [
        { to: '/funds', label: 'Funds', Icon: IconFunds },
      ] },
      { label: null, items: sharedBottom },
    ];
  }

  if (workspace === WORKSPACE_GENERAL) {
    // Internal initiatives (Dubai entity, CFC label…): projects + tasks only,
    // none of the trade deal apparatus.
    return [
      { label: null, items: sharedTop },
      { label: 'General', items: [
        { to: '/projects', label: 'Initiatives', Icon: IconProjects },
      ] },
      { label: null, items: sharedBottom },
    ];
  }

  return [
    { label: null, items: sharedTop },
    { label: 'Morocco — Trade', items: [
      { to: '/projects', label: 'Projects', Icon: IconProjects },
      { to: '/offers', label: 'Offers', Icon: IconContracts },
      { to: '/contracts', label: 'Contracts', Icon: IconContracts },
      { to: '/exclusivities', label: 'Exclusivities', Icon: IconContracts },
      { to: '/invoices', label: 'Invoices', Icon: IconFunds },
    ] },
    { label: null, items: sharedBottom },
  ];
}

const WorkspaceCtx = createContext(null);

export function WorkspaceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [workspace, setWorkspaceState] = useState(readStoredWorkspace);
  const [lastPaths, setLastPaths] = useState(readLastPaths);

  // When user lands on a domain-exclusive URL, auto-switch workspace.
  useEffect(() => {
    const owner = getWorkspaceForPath(location.pathname);
    if (owner && owner !== workspace) {
      setWorkspaceState(owner);
      try { localStorage.setItem(STORAGE_KEY, owner); } catch { /* */ }
    }
  }, [location.pathname, workspace]);

  // Remember last non-admin path per workspace for switch restore.
  useEffect(() => {
    const path = location.pathname + location.search;
    const owner = getWorkspaceForPath(location.pathname);
    const key = owner || workspace;
    if (
      path.startsWith('/settings')
      || path.startsWith('/account')
      || path.startsWith('/users')
      || path.startsWith('/login')
    ) {
      return;
    }
    setLastPaths((prev) => {
      if (prev[key] === path) return prev;
      const next = { ...prev, [key]: path };
      try { localStorage.setItem(LAST_PATH_KEY, JSON.stringify(next)); } catch { /* */ }
      return next;
    });
  }, [location.pathname, location.search, workspace]);

  const setWorkspace = useCallback((next, { navigate: doNav = true } = {}) => {
    if (!WORKSPACES.includes(next) || next === workspace) {
      if (next === workspace && doNav) {
        // already there — optional re-home
      }
      if (!WORKSPACES.includes(next)) return;
    }
    setWorkspaceState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* */ }

    if (!doNav) return;

    const currentOwner = getWorkspaceForPath(location.pathname);
    // Leaving a page that belongs to the other line → jump to remembered home
    if (currentOwner && currentOwner !== next) {
      const target = lastPaths[next] || WORKSPACE_HOME[next];
      navigate(target);
      return;
    }
    // On shared page: stay put, just switch chrome
  }, [workspace, location.pathname, lastPaths, navigate]);

  const switchWorkspace = useCallback((next) => {
    if (!WORKSPACES.includes(next) || next === workspace) return;
    setWorkspaceState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* */ }
    const target = lastPaths[next] || WORKSPACE_HOME[next];
    // If target is exclusive to the other side, fall back to home
    if (!isPathAllowedInWorkspace(target.split('?')[0], next)) {
      navigate(WORKSPACE_HOME[next]);
    } else {
      navigate(target);
    }
  }, [workspace, lastPaths, navigate]);

  const value = useMemo(() => ({
    workspace,
    isFunds: workspace === WORKSPACE_FUNDS,
    isTrade: workspace === WORKSPACE_TRADE,
    isGeneral: workspace === WORKSPACE_GENERAL,
    setWorkspace,
    switchWorkspace,
    navGroups: navGroupsFor(workspace),
    homePath: WORKSPACE_HOME[workspace],
    lastPaths,
  }), [workspace, setWorkspace, switchWorkspace, lastPaths]);

  return (
    <WorkspaceCtx.Provider value={value}>
      {children}
    </WorkspaceCtx.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
